import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EnrichedSong } from '../../types/PlaylistTypes';
import SongDisplayList from '../ViewAll/SongDisplayList';
import { encodeId, TruncateName } from '../HelperMethods';
import EllipsisDropdown from '../Common/EllipsisDropdown';

interface SongListManagerProps {
    songs: EnrichedSong[];
    onRemoveSong?: (songId: number) => void;
    onReorderSongs?: (songs: EnrichedSong[]) => void;
    showReordering?: boolean;
    isEditable?: boolean;
    emptyMessage?: string;
    showNumbers?: boolean;
    onSongClick?: (songId: number) => void;
}

const SongListManager: React.FC<SongListManagerProps> = ({
    songs,
    onRemoveSong,
    onReorderSongs,
    showReordering = false,
    isEditable = false,
    emptyMessage = "No songs in this playlist yet.",
    showNumbers = true,
    onSongClick
}) => {
    const navigate = useNavigate();

    const handleSongClick = (songId: number) => {
        if (onSongClick) {
            onSongClick(songId);
        } else {
            navigate(`/song/${encodeId(songId)}`);
        }
    };

    if (songs.length === 0) {
        return <p>{emptyMessage}</p>;
    }

    return (
        <div className="songs-list">
            {songs.map((song, index) => (
                <div
                    key={`${song.melon_song_id}-${index}`}
                    className="song-item"
                    onClick={!isEditable ? () => handleSongClick(song.melon_song_id) : undefined}
                    style={{ cursor: !isEditable ? 'pointer' : 'default' }}
                >
                    {showNumbers && (
                        <span className="song-number">{index + 1}</span>
                    )}

                    <div className="song-display-wrapper">
                        {isEditable ? (
                            // Edit mode - use SongDisplayList for consistency
                            <>
                                <SongDisplayList
                                    song={{
                                        melon_song_id: song.melon_song_id,
                                        song_id: song.melon_song_id,
                                        is_title: song.is_title,
                                        melon_album_id: song.melon_album_id,
                                        s3_picture_url: song.s3_picture_url || "/dubuFace.jpg",
                                        release_date: song.release_date || "",
                                        english_name: song.english_name || song.name || ""
                                    }}
                                    name={song.english_name || song.name || ""}
                                    s3_picture_url={song.s3_picture_url || "/dubuFace.jpg"}
                                    year=""
                                />
                                <div>
                                    <b>{song.english_name || song.name}</b>
                                </div>
                            </>
                        ) : (
                            // View mode - use the original display logic
                            <>
                                <div
                                    className="album-display-photo-wrapper"
                                    style={{
                                        backgroundImage: `url(${song.s3_picture_url || "/dubuFace260.jpg"})`,
                                    }}
                                />
                                <div className="song-playlist-details">
                                    {song.artist_ids && song.artist_ids.length === 1 && (
                                        <h6>
                                            <Link
                                                key={song.artist_ids.toString()}
                                                state={{ id: song.artist_ids }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    window.location.href = `/artist/${encodeId(song.artist_ids[0])}`;
                                                }}
                                                to=""
                                            >
                                                {TruncateName(song.artist_names?.[0] || song.artist_english_name || "Unknown Artist", 30)}
                                            </Link>
                                        </h6>
                                    )}
                                    {song.artist_ids && song.artist_ids.length > 1 && (
                                        <h6>
                                            {TruncateName(song.artist_names?.join(", ") || song.artist_english_name || "Unknown Artist", 30)}
                                        </h6>
                                    )}
                                    <Link
                                        key={song.melon_song_id}
                                        to={`/song/${encodeId(song.melon_song_id)}`}
                                        state={{ song_id: song.melon_song_id, album_id: song.melon_album_id }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {song.is_title && (
                                            <p>
                                                {song.english_name && "✰ "}
                                                <strong>{song.english_name || song.name}</strong>
                                            </p>
                                        )}
                                        {!song.is_title && (
                                            <p>
                                                <strong>{song.english_name || song.name}</strong>
                                            </p>
                                        )}
                                    </Link>
                                </div>
                            </>
                        )}
                    </div>

                    {isEditable && (
                        <div className="song-info">
                            {song.album_english_name && (
                                <div className="song-album">
                                    <div className="song-artist">
                                        {song.artist_names?.join(", ") || song.artist_english_name}
                                    </div>
                                    {song.album_english_name}
                                </div>
                            )}
                        </div>
                    )}

                    {onRemoveSong && (
                        <button
                            className={isEditable ? "remove-song-button" : "remove-song-button-new"}
                            onClick={(e) => {
                                e.stopPropagation();
                                onRemoveSong(song.melon_song_id);
                            }}
                        >
                            <i className="fa fa-trash-o" aria-hidden="true"></i>
                        </button>
                    )}

                    {!isEditable && (
                        <EllipsisDropdown
                            songId={song.melon_song_id}
                            songName={song.english_name || song.name || ''}
                        />
                    )}
                </div>
            ))}
        </div>
    );
};

export default SongListManager; 